import { type EvaluationContext, OpenFeature } from '@openfeature/web-sdk'
import { ReflagBrowserSDKProvider } from '@reflag/openfeature-browser-provider'

const publishableKey = import.meta.env.VITE_REFLAG_PUBLISHABLE_KEY

export interface FeatureFlagUser {
  id: string
  email?: string | null
  name?: string | null
}

export interface FeatureFlagCompany {
  id: string
  name: string
  plan?: string | null
}

let currentUser: FeatureFlagUser | null = null
let currentCompany: FeatureFlagCompany | null = null

/**
 * Registers Reflag as the OpenFeature provider. Without a publishable key the
 * default no-op provider stays active, so every flag resolves to the default
 * value passed at the call site.
 */
export function initFeatureFlags() {
  if (!publishableKey) return
  OpenFeature.setProvider(new ReflagBrowserSDKProvider({ publishableKey }))
}

/**
 * Maps the signed-in user and active workspace onto the OpenFeature context
 * keys read by Reflag's default context translator (`targetingKey` → user,
 * `company*` → company).
 */
export function buildFeatureFlagContext(
  user: FeatureFlagUser | null,
  company: FeatureFlagCompany | null,
): EvaluationContext {
  const context: EvaluationContext = {}
  if (user) {
    context.targetingKey = user.id
    if (user.email) context.email = user.email
    if (user.name) context.name = user.name
  }
  if (company) {
    context.companyId = company.id
    context.companyName = company.name
    if (company.plan) context.companyPlan = company.plan
  }
  return context
}

function syncContext() {
  void OpenFeature.setContext(
    buildFeatureFlagContext(currentUser, currentCompany),
  )
}

export function setFeatureFlagUser(user: FeatureFlagUser | null) {
  currentUser = user
  if (!user) currentCompany = null
  syncContext()
}

export function setFeatureFlagCompany(company: FeatureFlagCompany | null) {
  currentCompany = company
  syncContext()
}
